import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { ApiError } from '../../services/api';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { ROUTES } from '../../utils/constants';
import { useToast } from '../../hooks/useToast';

export function LoginForm() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate(ROUTES.DASHBOARD);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError("Your account is pending approval. You'll receive an email once access is granted.");
      } else {
        const message = 'Login failed. Check your email and password.';
        setError(message);
        showToast(message, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      <Input
        type="email"
        label={t('login.email')}
        placeholder={t('login.emailPlaceholder')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoComplete="email"
      />

      <Input
        type="password"
        label={t('login.password')}
        placeholder={t('login.passwordPlaceholder')}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        autoComplete="current-password"
      />

      <div className="flex items-center justify-between">
        <Link
          to={ROUTES.RESET_PASSWORD}
          className="text-sm text-primary-700 hover:text-accent-600"
        >
          {t('login.forgotPassword')}
        </Link>
      </div>

      <Button type="submit" className="w-full" loading={isLoading}>
        {t('login.loginButton')}
      </Button>
    </form>
  );
}
